import { ContentArea } from '@calendly/ui/components/content-area';
import { RefObject, ReactNode, createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCenterPanelLayoutMode } from './centerPanelLayoutContext';
const filterGradientCss = "f1rl1b0m";

/*
 * Full-bleed wrapper reserving the scrollbar gutter, mirroring PageHeader's
 * sticky container so filter content aligns with the scroll region.
 */
const filterChromeCss = "fylgf4h";
const filterOutletScrollCss = "f13ycopn";
const filterRendererBleedCss = "f90z0b2";
const filterRendererContentCss = "f1x617dx";
type PageFilterContextType = {
  filterRef: RefObject<HTMLDivElement | null>;
  isRendered: boolean;
  refBound: boolean;
  setIsRendered: (isRendered: boolean) => void;
  setFilterRef: (ref: HTMLDivElement | null) => void;
};
const FilterContext = createContext<PageFilterContextType>({
  filterRef: {
    current: null
  },
  isRendered: false,
  refBound: false,
  setIsRendered: () => {},
  setFilterRef: () => {}
});
export const PageFilterContext = ({
  children
}: {
  children: ReactNode;
}) => {
  const filterRef = useRef<HTMLDivElement | null>(null);
  const [isRendered, setIsRendered] = useState(false);
  const [refBound, setRefBound] = useState(false);
  const setFilterRef = useCallback((ref: HTMLDivElement | null) => {
    setRefBound(!!ref);
    if (filterRef.current !== ref) {
      filterRef.current = ref;
    }
  }, []);
  const data = useMemo(() => ({
    filterRef,
    setFilterRef,
    isRendered,
    refBound,
    setIsRendered
  }), [isRendered, refBound, setFilterRef]);
  return <FilterContext.Provider value={data}>
      {children}
    </FilterContext.Provider>;
};
export const usePageFilterContext = () => useContext(FilterContext);

/**
 * Renders the filter slot in the top chrome of `CenterPanel`.
 * Mounts only when a `PageFilterRenderer` is active — no empty space otherwise.
 */
export const PageFilterOutlet = () => {
  const {
    setFilterRef,
    isRendered
  } = usePageFilterContext();
  const layout = useCenterPanelLayoutMode() ?? 'default';
  if (!isRendered) return null;
  return <div className={filterChromeCss}>
      <ContentArea isWide={layout === 'wide'} padding="inline">
        <div ref={setFilterRef} data-testid="page-filter-outlet" className={filterOutletScrollCss} />
      </ContentArea>
    </div>;
};

/**
 * Renders a gradient overlay at the top of the scroll area when a filter is active.
 * Must be placed as a sibling of `topChromeCss` in `CenterPanel` so it is not
 * clipped by that element's `overflow: hidden`.
 */
export const PageFilterGradient = () => {
  const {
    isRendered
  } = usePageFilterContext();
  if (!isRendered) return null;
  return <div aria-hidden className={filterGradientCss} />;
};

/**
 * Portals children into the `PageFilterOutlet` in the top chrome.
 * Place this at the top of a page component to hoist filters out of the scroll container.
 *
 * @example
 * function MyPage() {
 *   return (
 *     <>
 *       <PageFilterRenderer>
 *         <MyFilterBar />
 *       </PageFilterRenderer>
 *       <PageContent />
 *     </>
 *   )
 * }
 */
export const PageFilterRenderer = ({
  children
}: {
  children: ReactNode;
}) => {
  const {
    filterRef,
    refBound,
    setIsRendered
  } = usePageFilterContext();
  useLayoutEffect(() => {
    setIsRendered(true);
    return () => setIsRendered(false);
  }, [setIsRendered]);
  if (refBound && filterRef.current) {
    return createPortal(<div className={filterRendererBleedCss}>
        <div className={filterRendererContentCss} data-testid="page-filter-renderer-content">
          {children}
        </div>
      </div>, filterRef.current);
  }
  return null;
};

require("./pageFilterContext.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./pageFilterContext.tsx");